import {useMemo, useEffect} from 'react';

import sdk from '@/sdk';
import * as store from '@/store';
import {rteHost, ServerConfig} from '@/config/routers';
import {getEditorImageProxyUrl} from '@/utils/document/editorImage';
import {getUploadPartialFileFunc} from '@/hooks/document/useUploadFile';
import {getMorphoService, serviceNames} from '@/rte/editor';
import {isInKuShare} from '@/utils/urlUtils';
import {getIsBaikeRepo} from '@/pages/baike/utils';

import {uploadToRTE, getBosConfig} from './utils';


export const useImageConfig = (demoMode: boolean, ocrReady: boolean) => {
    const kuShareOrBaike = isInKuShare() || getIsBaikeRepo(store.getCurDocument()?.repositoryGuid || '');
    const settingOpenOCR = !!sdk.user?.settings.newSettings.intelligent.openOCR;
    const visibleOCRBtn = settingOpenOCR && ocrReady && !kuShareOrBaike;
    const disableOCRBrowserPlugin = !demoMode && ocrReady;

    const imageConfig = useMemo(() => {
        return {
            showDownloadButton: () => true,
            getProxyUrl: getEditorImageProxyUrl,
            // 非内部链接，就要进行重新上传
            needUpload(link: string) {
                const isRteLink = link.includes(rteHost);
                return !isRteLink;
            },
            // 图片越权
            validateReferrer: (src: string) => /http(s)?:\/\/([a-z-0-9]+\.)+baidu(-int)?.com\.*/.test(src),
            // 图片添加
            addQueries(src: string): string {
                if (!/http(s)?:\/\/([a-z-0-9]+\.)+baidu(-int)?.com\.*/.test(src)) {
                    return src;
                }

                const {accessToken} = store.getData(store.KEYS.CUR_DOCUMENT_TOKEN) || {};
                try {
                    const urlObj = new URL(src);
                    urlObj.searchParams.set('sign', accessToken);
                    return urlObj.toString();
                } catch (err) {
                    console.error(err);
                    return src;
                }
            },
            tooltipOCREnable: visibleOCRBtn,
            browserPluginOCRDisable: disableOCRBrowserPlugin,
        };
    }, [disableOCRBrowserPlugin, visibleOCRBtn]);

    useEffect(() => {
        try {
            const config = getMorphoService(serviceNames.IConfigService);
            if (config) {
                config.state.imageConfig.next({
                    ...config.state.imageConfig.getValue(),
                    tooltipOCREnable: visibleOCRBtn,
                    browserPluginOCRDisable: disableOCRBrowserPlugin,
                });
            }
        } catch (err) {
            console.error(err);
        }
    }, [disableOCRBrowserPlugin, visibleOCRBtn]);

    return imageConfig;
};

export const useBottomImageConfig = () => {
    return useMemo(() => {
        const entrypoint = ServerConfig.rte;
        const docGuid = store.getData(store.KEYS.CUR_DOCUMENT).docGuid;
        return {
            showDownloadButton: () => true,
            getProxyUrl: getEditorImageProxyUrl,
            // 非内部链接，就要进行重新上传
            needUpload(link: string) {
                const isRteLink = link.includes(rteHost);
                return !isRteLink;
            },
            // 图片越权
            validateReferrer: (src: string) => /http(s)?:\/\/([a-z-0-9]+\.)+baidu(-int)?.com\.*/.test(src),
            // 图片添加
            addQueries(src: string): string {
                if (!/http(s)?:\/\/([a-z-0-9]+\.)+baidu(-int)?.com\.*/.test(src)) {
                    return src;
                }

                const {accessToken} = store.getData(store.KEYS.CUR_DOCUMENT_TOKEN) || {};
                try {
                    const urlObj = new URL(src);
                    urlObj.searchParams.set('sign', accessToken);
                    return urlObj.toString();
                } catch (err) {
                    console.error(err);
                    return src;
                }
            },
            upload: async function (
                file: File, {type, setProgress, id}: {type: string, setProgress: (n: number) => void, id:string }) {
                // 外链图片
                if (type === 'link') {
                    const requestParams = {
                        url: file,
                        docGuid: docGuid,
                    };
                    const {accessToken: rteToken} = store.getData(store.KEYS.CUR_DOCUMENT_TOKEN);
                    try {
                        const result = await sdk.serverBase.request({
                            method: 'post',
                            url: entrypoint + '/wiki/attach/image/transfer',
                            data: requestParams,
                            headers: {
                                Authorization: rteToken,
                            },
                        });
                        return {
                            fileId: docGuid,
                            url: result.transformedUrl,
                        };
                    }
                    catch (error) {
                        console.log(error);
                        return Promise.reject(error);
                    }
                } else if (file instanceof File || file as any instanceof Blob) {
                    try {
                        const result = await uploadToRTE(file, {setProgress}, 'image');
                        return {
                            fileInfo: file,
                            ...result,
                        };
                    } catch (error) {
                        // TODO:为了解决不同图片名称相同上传会发生覆盖，将文件名 + 时间戳 取md5，导致下载图片名变成乱码，如果要启用这段逻辑，需要server修改
                        // 上传附件下载文件名没有问题
                        console.log('原始上传功能出错，进入图片兜底策略');
                        try {
                            // 获取bos配置
                            const {bosConfig, bucket} = await getBosConfig({docId: docGuid, entrypoint});

                            // 动态获取分片上传方法
                            const uploadPartialFile = await getUploadPartialFileFunc();
                            const bosResult = await uploadPartialFile(file, bosConfig, bucket, setProgress);
                            // 分片上传
                            // 上传完成后，通知server，获取attachId
                            const result = await sdk.serverBase.request({
                                method: 'post',
                                url: entrypoint + '/wiki/attach/attachments/infoSave',
                                data: {
                                    bucket,
                                    object: bosResult.body.key,
                                    fileName: file.name,
                                    fileSize: file.size,
                                    docGuid: docGuid,
                                },
                            });

                            return {
                                fileInfo: file,
                                fileId: result.attachId,
                                url: bosResult.body.location,
                            };
                        } catch (error) {
                            console.log(error);
                            return Promise.reject(error);
                        }
                        // return Promise.reject(error);
                    }
                }
            },
            onChange: function (element: any, imageData: any) {
                const {src} = element;
                const attachId = src.split('?')[1]?.split('=')[1];
                const {x, y, width, height, rotate} = imageData;
                // 如果没有解析不出attachId, 则返回空字符串
                if (!attachId) {
                    return '';
                }

                const res = `${src}${src.indexOf('?') > 0 ? '&' : '?'}
                    x-bce-process=image/crop,x_${x},y_${y},w_${width},h_${height}/rotate,a_${rotate}`;

                return res;
            },
            // 目前只需要上传和预览图片
            // download: async function (url: string, scope: boolean) {
            //     const isRteUrl = (url).includes('rte') || (url).includes('/wiki/attach');
            //     if (!isRteUrl) {
            //         if (scope) {
            //             return url;
            //         }
            //         downloadUrl(url);
            //         return;
            //     }
            //     try {
            //         const result = await sdk.serverBase.request({
            //             method: 'post',
            //             url: entrypoint + '/wiki/attach/image/downloadUrl',
            //             data: {
            //                 src: url,
            //                 docGuid: docGuid,
            //             },
            //         });
            //         if (result.downloadUrl) {
            //             if (scope) {
            //                 return result.downloadUrl;
            //             }
            //             downloadUrl(result.downloadUrl);
            //         } else {
            //             console.error(`没有正确返回参数downloadUrl: ${result.downloadUrl}`);
            //         }
            //     } catch (error: any) {
            //         console.error(error);
            //         const msg = error?.msg || error.message;
            //         msg && toast({
            //             type: ToastType.Error,
            //             content: msg,
            //         });
            //     }
            // },
            lazyLoad: true,
            imageGalleryMaxLength: 9,
        };
    }, []);
};
